import { useState } from 'react';
import type { LaterWhen } from '../../lib/api';
import { Button } from '../ui';
import { laterTodayOffered, pacificTomorrow } from './todoFormat';

interface Props {
  /** Called with the TOKEN he chose; the caller sends it. */
  onPick: (when: LaterWhen) => void;
  disabled?: boolean;
  /**
   * CHAT-DUET (2026-10-28): the trigger's look where it sits among pill buttons (the Morning chat's cards). Given, the
   * trigger is a plain button with these classes; left out, it is today's outline Button (the Tasks page, Robert's page).
   */
  triggerClassName?: string;
}

/**
 * Later (R11, his choices): hidden from reminders until then - the app's own, ⛔ GoHighLevel's due date is not changed.
 * ⭐ Each choice is a token ('later_today', 'tomorrow', 'monday' or the picked 'YYYY-MM-DD') and the SERVER turns it into
 * a time in Pacific: the browser's clock and zone never decide a business time.
 * Rendered as a fragment, so in a wrapping row of buttons the choices take a line of their own under them.
 */
export default function LaterMenu({ onPick, disabled = false, triggerClassName }: Props) {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [date, setDate] = useState('');
  const now = new Date();
  const firstDay = pacificTomorrow(now);
  // ⭐ YYYY-MM-DD strings compare the way the dates do. ⛔ An iPhone's picker fills in TODAY the moment it opens, whatever
  // `min` says (the 148th look) - so Set waits for a day the server will take, instead of a tap it answers with a refusal.
  const canSet = date !== '' && date >= firstDay;

  const pick = (when: LaterWhen) => {
    setOpen(false);
    setPicking(false);
    setDate('');
    onPick(when);
  };

  return (
    <>
      {triggerClassName ? (
        <button type="button" className={triggerClassName} aria-expanded={open} disabled={disabled} onClick={() => setOpen((o) => !o)}>
          Later
        </button>
      ) : (
        <Button size="sm" variant="outline" aria-expanded={open} disabled={disabled} onClick={() => setOpen((o) => !o)}>
          Later
        </Button>
      )}
      {open && (
        <div role="group" aria-label="Remind me" className="flex basis-full flex-wrap items-center gap-2">
          {laterTodayOffered(now) && (
            <Button size="sm" variant="ghost" onClick={() => pick('later_today')}>Later today</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => pick('tomorrow')}>Tomorrow morning</Button>
          <Button size="sm" variant="ghost" onClick={() => pick('monday')}>Monday morning</Button>
          {/* ⛔ Opens on tomorrow: an EMPTY date box draws as a sliver on an iPhone, with nothing to tap (the 148th look). */}
          <Button size="sm" variant="ghost" aria-expanded={picking} onClick={() => { setPicking(true); setDate((d) => d || firstDay); }}>
            Pick a date
          </Button>
          {picking && (
            <span className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs text-gray-600">
                Remind me on
                <input
                  type="date"
                  min={firstDay}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="min-w-[9rem] rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </label>
              {/* A button, not the input's change: typing a date fires a change at every digit of the year. */}
              <Button size="sm" disabled={!canSet} onClick={() => pick(date)}>Set</Button>
            </span>
          )}
        </div>
      )}
    </>
  );
}
